import React, { createContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { narrativeKeys, getNarrativeTexts, processNarrativeText, getNarrativeDelay } from '../data/narrativeContent';

export interface NarrativeContextValue {
  currentTexts: string[];
  currentKey: string | null;
  currentDelay: number;
  advanceNarrative: () => void;
  setVariables: (variables: Record<string, any>) => void;
  reset: () => void;
  jumpToKey: (key: string) => void;
}

export const NarrativeContext = createContext<NarrativeContextValue | null>(null);

interface NarrativeProviderProps {
  children: ReactNode;
}

export const NarrativeProvider: React.FC<NarrativeProviderProps> = ({ children }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [variables, setVariables] = useState<Record<string, any>>({});

  const currentKey = useMemo(() => {
    return narrativeKeys[currentIndex] || null;
  }, [currentIndex]);

  const currentTexts = useMemo(() => {
    if (!currentKey) return [];
    const rawTexts = getNarrativeTexts(currentKey);
    return rawTexts.map(text => processNarrativeText(text, variables));
  }, [currentKey, variables]);

  const currentDelay = useMemo(() => {
    return currentKey ? getNarrativeDelay(currentKey) : 2000;
  }, [currentKey]);

  const advanceNarrative = useCallback(() => {
    setCurrentIndex(prev => {
      const nextIndex = prev + 1;
      return nextIndex < narrativeKeys.length ? nextIndex : prev;
    });
  }, []);

  const reset = useCallback(() => {
    setCurrentIndex(0);
    setVariables({});
  }, []);

  const jumpToKey = useCallback((key: string) => {
    const index = narrativeKeys.indexOf(key);
    if (index !== -1) {
      setCurrentIndex(index);
      console.log(`Jumped to ${key} at index ${index}`);
    } else {
      console.error(`Narrative key not found: ${key}`);
    }
  }, []);

  const value: NarrativeContextValue = {
    currentTexts,
    currentKey,
    currentDelay,
    advanceNarrative,
    setVariables,
    reset,
    jumpToKey,
  };

  return (
    <NarrativeContext.Provider value={value}>
      {children}
    </NarrativeContext.Provider>
  );
};